import _extends from "@babel/runtime/helpers/extends";
import pt from 'prop-types';
import React from 'react';
import cn from 'classnames';
import Portal from '@platform-ui/portal';
import dataQa from '@platform-ui/dataQa';
import { layers } from '@platform-ui/constants';
import { isTouchDevice } from '@platform-ui/device-info';
import { useZoomContext } from '@platform-ui/dom-utils';
import PopoverBlock from './PopoverBlock';
import { isInPopover } from './utils';
import s from './Popover.module.css';
import BasePopover, { noop } from './BasePopover';
export const propTypes = {
  direction: pt.oneOfType([pt.oneOf(['left', 'top', 'right', 'bottom']), pt.array]),
  align: pt.oneOf(['start', 'center', 'end', 'stretch'])
};
const isOpened = ({
  active,
  isInactive
}) => Boolean(!isInactive && active);
class Popover extends BasePopover {
  constructor(props) {
    super(props);
    this.getTargetElement = () => {
      return this.childrenBlock;
    };
    this.getIsActive = () => {
      return this.state.active;
    };
    this.getActiveChanged = (prevProps, prevState) => {
      return prevState.active !== this.getIsActive();
    };
    this.open = () => {
      if (!this.state.active) {
        this.setState({
          active: true
        });
        this.props.onShow(_extends({}, this.state, {
          active: true
        }));
      }
    };
    this.close = ({
      isActive
    } = this.props) => {
      if (!isActive) {
        this.setState({
          active: false
        });
      }
      this.props.onClose(_extends({}, this.state, {
        active: false
      }));
    };
    this.toggle = ({
      active
    } = this.state) => (active ? this.close : this.open)();
    this.setChildrenBlock = elem => {
      this.childrenBlock = elem;
    };
    this.handleClick = event => {
      const {
        preventDefaultClick,
        stopPropagationClick,
        closeOnPopoverClick,
        onClick
      } = this.props;
      if (preventDefaultClick) {
        event.preventDefault();
      }
      if (stopPropagationClick) {
        event.stopPropagation();
      }
      if (this.getToggleOnClick() && isInPopover(event.target, this.childrenBlock)) {
        this.toggle();
      } else if (closeOnPopoverClick && isInPopover(event.target, this.popover)) {
        this.close();
      }
      onClick(event);
    };
    this.handleChildrenOver = e => {
      var _this$props$onMouseEn, _this$props;
      if (!this.props.noBubbleHover || isInPopover(e.target, this.childrenBlock)) {
        clearTimeout(this.closeTimeout);
      }
      if (!this.props.hoverDelay) {
        this.open();
      } else {
        this.openTimeout = setTimeout(this.open, this.props.hoverDelay);
      }
      (_this$props$onMouseEn = (_this$props = this.props).onMouseEnter) == null || _this$props$onMouseEn.call(_this$props, e);
    };
    this.handleChildrenOut = e => {
      var _this$props$onMouseLe, _this$props2;
      clearTimeout(this.openTimeout);
      this.closeTimeout = setTimeout(this.close, this.props.fadeDelay);
      (_this$props$onMouseLe = (_this$props2 = this.props).onMouseLeave) == null || _this$props$onMouseLe.call(_this$props2, e);
    };
    this.state = {
      active: props.isActive || false,
      toggleOnClick: this.getToggleOnClick()
    };
    props.getSetPositionFunc(this.setTooltipPosition);
    props.getSetTargetBlockFunc(this.setChildrenBlock);
  }
  static getDerivedStateFromProps(props, state) {
    if (state.active !== props.isActive && props.isActive !== undefined) {
      return {
        active: props.isActive
      };
    }
    return null;
  }
  getToggleOnClick() {
    return this.props.toggleOnClick || isTouchDevice();
  }
  render() {
    const {
      ariaOwnsId,
      isInactive,
      children,
      dataQaType,
      popoverBlock,
      renderPopoverBlock,
      increasedTouchZone,
      zIndex,
      wide,
      role,
      ariaDescribedBy,
      animated,
      disableToggleOnInsideEvents,
      to,
      disabled,
      toBody,
      blockStyles = {},
      portalTagName,
      UNSAFE_className,
      variant,
      align
    } = this.props;

    // UIKIT-5993 когда поповер находится в скролл контейнере наш PortalWrapper
    // тянется только до границ скролл контейнера, а не по ширине конетента (т.е. контент может быть длиннее)
    // из-за этого когда мы показываем поповер за границами PortalWrapper
    // браузер начианет переносить его содержимое, чтобы оно максимально схлопнулось
    // поэтому ставим ему max-content если другой ширины нет.
    const {
      direction,
      left,
      top,
      active,
      width = 'max-content',
      height,
      touchzone,
      cornerLeft,
      cornerTop
    } = this.state;
    const visibility = top !== undefined && left !== undefined ? 'visible' : 'hidden';
    const toggleOnClick = this.getToggleOnClick();
    const shouldIncreaseTouchzone = typeof increasedTouchZone === 'boolean' ? increasedTouchZone : !toggleOnClick;
    return /*#__PURE__*/React.createElement("span", Object.assign({}, dataQa({
      dataQaType
    }), {
      className: cn({
        [s.popover]: true,
        [s.popover_wide]: wide,
        [s.popover_disabled]: disabled
      }),
      role: role,
      "aria-describedby": ariaDescribedBy,
      "aria-owns": ariaOwnsId
    }, !disableToggleOnInsideEvents ? {
      onClick: this.handleClick,
      onMouseEnter: !toggleOnClick ? this.handleChildrenOver : undefined,
      onMouseLeave: !toggleOnClick ? this.handleChildrenOut : undefined
    } : {}, {
      onMouseMove: this.props.onMouseMove
    }), /*#__PURE__*/React.createElement("span", {
      ref: this.setChildrenBlock,
      className: s.childrenBlock,
      "data-qa-type": `${dataQaType}.children`
    }, children), /*#__PURE__*/React.createElement(Portal, {
      to: "body"
    }, isOpened({
      active,
      isInactive
    }) && this.renderCorrectionBlock()), /*#__PURE__*/React.createElement(Portal, {
      to: to,
      toBody: toBody,
      exitRef: this.setPopoverContainer,
      tagName: portalTagName
    }, /*#__PURE__*/React.createElement(PopoverBlock, {
      popoverBlock: popoverBlock || renderPopoverBlock(this.close, {
        direction
      }),
      increasedTouchZone: shouldIncreaseTouchzone && touchzone,
      refCallback: this.setPopover,
      direction: direction,
      align: align,
      animated: animated,
      variant: variant,
      dataQaType: `${dataQaType}.popoverBlock`,
      style: _extends({
        top,
        left,
        zIndex: typeof zIndex === 'number' || typeof zIndex === 'undefined' ? zIndex : layers[zIndex],
        width,
        height,
        visibility,
        '--tui-popover-corner-left': cornerLeft ? `${cornerLeft}px` : undefined,
        '--tui-popover-corner-top': cornerTop ? `${cornerTop}px` : undefined
      }, blockStyles),
      className: UNSAFE_className,
      opened: isOpened({
        active,
        isInactive
      })
    })));
  }
}
Popover.propTypes = _extends({}, BasePopover.propTypes, {
  /**
   * конент блока, к которому прикреплена всплывашка
   */
  children: pt.node,
  /**
   * Задает атрибут aria-owns, принимает id блока-владельца
   */
  ariaOwnsId: pt.string,
  /**
   * Принудительно скрывает содержимое prop popoverContent
   */
  isInactive: pt.bool,
  /**
   * Флаг, растягивает компонент на 100% по W x H
   */
  wide: pt.bool,
  /**
   * Aтрибут role компонента
   */
  role: pt.string,
  /**
   * Атрибут aria-describedby компонента
   */
  ariaDescribedBy: pt.string,
  /**
   * Отключает показ/скрытие по событиям внутри Popover (onClick, onMouseEnter, onMouseLeave)
   * [UIKIT-5013]
   */
  disableToggleOnInsideEvents: pt.bool,
  /**
   * Включает состояние `disabled`
   */
  disabled: pt.bool,
  /**
   * Скрывать поповер по ховеру на него
   */
  noBubbleHover: pt.bool,
  /**
   * Задержка появления при ховере
   */
  hoverDelay: pt.number,
  /**
   * появление только по клику на элемент
   */
  toggleOnClick: pt.bool,
  /**
   * Флаг для preventDefault на событие click
   */
  preventDefaultClick: pt.bool,
  /**
   * Флаг для stopPropagation на событие click
   */
  stopPropagationClick: pt.bool,
  /**
   * Позволяет получить снаружи функцию для установки childrenBlock
   */
  getSetTargetBlockFunc: pt.func,
  /**
   * Обработчик срабатывает при клике на Popover
   */
  onClick: pt.func,
  /**
   * Обработчик срабатывает при деактивации Popover'a
   */
  onClose: pt.func,
  /**
   * Обработчик срабатывает при активации Popover'a
   */
  onShow: pt.func,
  /**
   * Задержка закрытия поповера после того как курсор уйдет с триггера
   */
  fadeDelay: pt.number,
  /**
   * Variant
   *
   * На десктопе и мобиле различаются анимации появления/исчезновения
   *
   * @default desktop
   */
  variant: pt.string
});
Popover.defaultProps = _extends({}, BasePopover.defaultProps, {
  ariaOwnsId: null,
  isInactive: false,
  wide: false,
  role: null,
  ariaDescribedBy: null,
  disableToggleOnInsideEvents: false,
  disabled: false,
  noBubbleHover: false,
  hoverDelay: 0,
  toggleOnClick: false,
  preventDefaultClick: false,
  stopPropagationClick: false,
  variant: 'desktop',
  getSetTargetBlockFunc: noop,
  onClick: noop,
  onClose: noop,
  onShow: noop,
  fadeDelay: 100
});
export default (props => {
  const zoomContext = useZoomContext();
  return /*#__PURE__*/React.createElement(Popover, Object.assign({}, props, {
    zoomParams: zoomContext.zoom
  }));
});